/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import type { TableCellNode, TableDOMCell, TableMapType } from '@lexical/table';
import type { LexicalEditor, NodeKey } from 'lexical';
import type { JSX } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';
import {
  $computeTableMapSkipCellCheck,
  $getTableNodeFromLexicalNodeOrThrow,
  $getTableRowIndexFromTableCellNode,
  $isTableCellNode,
  $isTableRowNode,
  getDOMCellFromTarget,
  getTableElement,
  TableNode,
} from '@lexical/table';
import { calculateZoomLevel, mergeRegister } from '@lexical/utils';
import {
  $getNearestNodeFromDOMNode,
  isHTMLElement,
  SKIP_SCROLL_INTO_VIEW_TAG,
} from 'lexical';
import * as React from 'react';
import {
  CSSProperties,
  PointerEventHandler,
  ReactPortal,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

type PointerPosition = {
  x: number;
  y: number;
};

type PointerDraggingDirection = 'right' | 'bottom';

const MIN_ROW_HEIGHT = 33;
// 엑셀 기본 열폭(64px)보다 크면 붙여넣은 표에서 좁히는 드래그가 통째로 무시됨.
// lexical_editor.scss 의 tableCell min-width 와 같은 값 유지 — 더 작으면 지정 폭과 렌더 폭이 어긋남.
const MIN_COLUMN_WIDTH = 40;

/** border-collapse 표의 좌우 외곽 보더 합(각 1px) */
const TABLE_OUTER_BORDER = 2;

/**
 * 표가 쓸 수 있는 본문 폭. root.clientWidth 단독 사용 금지 —
 * 넓은 표에 밀려 root 가 늘어난 상태면 그 폭으로 다시 정규화돼 폭이 계단식으로 커짐.
 * 스크롤 컨테이너까지 올라가 최소 폭을 기준으로 삼음.
 */
export const getAvailableWidth = (editor: LexicalEditor): number => {
  const root = editor.getRootElement();
  if (!root) return 0;
  const style = getComputedStyle(root);
  const padX =
    parseFloat(style.paddingLeft || '0') +
    parseFloat(style.paddingRight || '0');
  let limit = root.clientWidth;
  let el = root.parentElement;
  while (el && el !== document.body) {
    if (el.clientWidth > 0) limit = Math.min(limit, el.clientWidth);
    el = el.parentElement;
  }
  // 외곽 보더가 표 박스에 포함돼 합계를 본문 폭에 딱 맞추면 삐져나감
  return Math.max(0, limit - padX - TABLE_OUTER_BORDER);
};

const sameWidths = (
  a: readonly number[] | null | undefined,
  b: readonly number[],
) => !!a && a.length === b.length && a.every((w, i) => w === b[i]);

const getCellColumnIndex = (
  tableCellNode: TableCellNode,
  tableMap: TableMapType,
) => {
  for (let row = 0; row < tableMap.length; row++) {
    for (let column = 0; column < tableMap[row].length; column++) {
      if (tableMap[row][column].cell === tableCellNode) {
        return column;
      }
    }
  }
};

/** 비율 유지하며 합계를 total 로 맞춤. 최소폭 미달 열은 고정하고 나머지에 재분배(수렴까지). */
const distributeToTotal = (
  base: readonly number[],
  total: number,
): number[] => {
  const columnCount = base.length;
  // 최소폭을 다 줄 수 없을 만큼 열이 많으면 최소폭 포기
  const honorMin = columnCount * MIN_COLUMN_WIDTH <= total;
  const pinned = new Array<boolean>(columnCount).fill(false);
  if (honorMin) {
    for (let pass = 0; pass < columnCount; pass += 1) {
      const pinnedTotal = pinned.reduce(
        (n, isPinned) => n + (isPinned ? MIN_COLUMN_WIDTH : 0),
        0,
      );
      const freeSum = base.reduce((a, w, i) => a + (pinned[i] ? 0 : w), 0);
      if (freeSum <= 0) break;
      let changed = false;
      for (let i = 0; i < columnCount; i += 1) {
        if (pinned[i]) continue;
        if ((base[i] / freeSum) * (total - pinnedTotal) < MIN_COLUMN_WIDTH) {
          pinned[i] = true;
          changed = true;
        }
      }
      if (!changed) break;
    }
  }
  const pinnedTotal = pinned.reduce(
    (n, isPinned) => n + (isPinned ? MIN_COLUMN_WIDTH : 0),
    0,
  );
  const freeSum = base.reduce((a, w, i) => a + (pinned[i] ? 0 : w), 0);
  const out = base.map((w, i) => {
    if (pinned[i]) return MIN_COLUMN_WIDTH;
    if (freeSum <= 0) return Math.max(1, (total - pinnedTotal) / columnCount);
    return (w / freeSum) * (total - pinnedTotal);
  });

  const rounded = out.map((w) => Math.max(1, Math.floor(w)));
  const diff = total - rounded.reduce((a, b) => a + b, 0);
  if (diff !== 0) {
    // 반올림 오차는 가장 넓은 비고정 열에 몰아 합계를 정확히 맞춤
    let widest = -1;
    for (let i = 0; i < rounded.length; i += 1) {
      if (pinned[i]) continue;
      if (widest < 0 || rounded[i] > rounded[widest]) widest = i;
    }
    if (widest < 0) widest = 0;
    rounded[widest] = Math.max(1, rounded[widest] + diff);
  }
  return rounded;
};

/**
 * colWidths 교정. 손댈 필요 없으면 null.
 *
 * 교정 대상은 문서 데이터가 잘못된 경우로 한정:
 *  - 길이 불일치·빈 칸(엑셀 span) — 그 열이 조절 불가해지고 폭도 한 칸씩 밀려 표시가 틀림
 *  - 최소폭 미달 — CSS min-width 에 걸려 지정↔렌더가 어긋남
 *
 * 합계가 본문 폭을 넘는 건 교정 대상이 아님. 문서가 잘못된 게 아니라 지금 창이 좁은 것뿐이고,
 * 렌더는 컨테이너에서 이미 캡되며 드래그도 배율 보정으로 1:1 유지됨.
 * 교정하면 넓은 화면에서 만든 표가 좁은 화면에서 열었을 때 영구 축소됨.
 */
const normalizeColWidths = (
  current: readonly number[] | null | undefined,
  columnCount: number,
  available: number,
): number[] | null => {
  if (columnCount <= 0) return null;

  const isValid = (w: unknown): w is number =>
    typeof w === 'number' && Number.isFinite(w) && w > 0;

  const lengthOk = !!current && current.length === columnCount;
  const allValid = lengthOk && current.every(isValid);
  const currentSum = allValid ? current.reduce((a, b) => a + b, 0) : 0;
  const needsRebuild = !allValid;
  const belowMin = allValid && current.some((w) => w < MIN_COLUMN_WIDTH);
  if (!needsRebuild && !belowMin) return null;

  const valid = (current || []).filter(isValid);
  const fallback = valid.length
    ? valid.reduce((a, b) => a + b, 0) / valid.length
    : available > 0
    ? available / columnCount
    : MIN_COLUMN_WIDTH;
  const base = Array.from({ length: columnCount }, (_, i) => {
    const w = current?.[i];
    return isValid(w) ? w : fallback;
  });

  // 레이아웃 전(폭 0)이면 길이만 맞추고 합계 보정은 다음 transform 으로
  if (available <= 0) return base.map((w) => Math.max(1, Math.round(w)));

  // 최소폭 미달만 문제일 때는 현재 총폭 유지 — 본문 폭으로 맞추면 사용자가 조절해 둔 폭이 바뀜
  const total = needsRebuild
    ? Math.round(available)
    : Math.max(Math.round(currentSum), columnCount * MIN_COLUMN_WIDTH);
  return distributeToTotal(base, total);
};

function TableCellResizer({ editor }: { editor: LexicalEditor }): JSX.Element {
  const targetRef = useRef<HTMLElement | null>(null);
  const resizerRef = useRef<HTMLDivElement | null>(null);
  const tableRectRef = useRef<ClientRect | null>(null);
  const [hasTable, setHasTable] = useState(false);

  const pointerStartPosRef = useRef<PointerPosition | null>(null);
  const [pointerCurrentPos, updatePointerCurrentPos] =
    useState<PointerPosition | null>(null);

  const [activeCell, updateActiveCell] = useState<TableDOMCell | null>(null);
  const [draggingDirection, updateDraggingDirection] =
    useState<PointerDraggingDirection | null>(null);

  const resetState = useCallback(() => {
    updateActiveCell(null);
    targetRef.current = null;
    updateDraggingDirection(null);
    pointerStartPosRef.current = null;
    tableRectRef.current = null;
  }, []);

  useEffect(() => {
    const tableKeys = new Set<NodeKey>();
    return mergeRegister(
      editor.registerMutationListener(TableNode, (nodeMutations) => {
        for (const [nodeKey, mutation] of nodeMutations) {
          if (mutation === 'destroyed') {
            tableKeys.delete(nodeKey);
          } else {
            tableKeys.add(nodeKey);
          }
        }
        setHasTable(tableKeys.size > 0);
      }),
      editor.registerNodeTransform(TableNode, (tableNode) => {
        // 열너비가 없는 표뿐 아니라 길이·합계가 어긋난 표도 교정 대상 —
        // 짧은 배열을 통과시키면 뒤쪽 열이 영구히 조절 불가
        const current = tableNode.getColWidths();
        const next = normalizeColWidths(
          current,
          tableNode.getColumnCount(),
          getAvailableWidth(editor),
        );
        if (next && !sameWidths(current, next)) {
          tableNode.setColWidths(next);
        }
        return tableNode;
      }),
    );
  }, [editor]);

  useEffect(() => {
    if (!hasTable) {
      return;
    }

    const onPointerMove = (event: PointerEvent) => {
      const target = event.target;
      if (!isHTMLElement(target)) {
        return;
      }

      if (draggingDirection) {
        event.preventDefault();
        event.stopPropagation();
        updatePointerCurrentPos({
          x: event.clientX,
          y: event.clientY,
        });
        return;
      }
      if (resizerRef.current && resizerRef.current.contains(target)) {
        return;
      }

      if (targetRef.current !== target) {
        targetRef.current = target;
        const cell = getDOMCellFromTarget(target);

        if (cell && activeCell !== cell) {
          editor.getEditorState().read(
            () => {
              const tableCellNode = $getNearestNodeFromDOMNode(cell.elem);
              if (!tableCellNode) {
                throw new Error('TableCellResizer: Table cell node not found.');
              }

              const tableNode =
                $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
              const tableElement = getTableElement(
                tableNode,
                editor.getElementByKey(tableNode.getKey()),
              );

              if (!tableElement) {
                throw new Error('TableCellResizer: Table element not found.');
              }

              targetRef.current = target;
              tableRectRef.current = tableElement.getBoundingClientRect();
              updateActiveCell(cell);
            },
            { editor },
          );
        } else if (cell == null) {
          resetState();
        }
      }
    };

    const onPointerDown = (event: PointerEvent) => {
      const isTouchEvent = event.pointerType === 'touch';
      if (isTouchEvent) {
        onPointerMove(event);
      }
    };

    const resizerContainer = resizerRef.current;
    resizerContainer?.addEventListener('pointermove', onPointerMove, {
      capture: true,
    });

    const removeRootListener = editor.registerRootListener(
      (rootElement, prevRootElement) => {
        prevRootElement?.removeEventListener('pointermove', onPointerMove);
        prevRootElement?.removeEventListener('pointerdown', onPointerDown);
        rootElement?.addEventListener('pointermove', onPointerMove);
        rootElement?.addEventListener('pointerdown', onPointerDown);
      },
    );

    return () => {
      removeRootListener();
      resizerContainer?.removeEventListener('pointermove', onPointerMove);
    };
  }, [activeCell, draggingDirection, editor, resetState, hasTable]);

  const isHeightChanging = (direction: PointerDraggingDirection) => {
    if (direction === 'bottom') {
      return true;
    }
    return false;
  };

  const updateRowHeight = useCallback(
    (heightChange: number) => {
      if (!activeCell) {
        throw new Error('TableCellResizer: Expected active cell.');
      }

      editor.update(
        () => {
          const tableCellNode = $getNearestNodeFromDOMNode(activeCell.elem);
          if (!$isTableCellNode(tableCellNode)) {
            throw new Error('TableCellResizer: Table cell node not found.');
          }

          const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
          const baseRowIndex =
            $getTableRowIndexFromTableCellNode(tableCellNode);
          const tableRows = tableNode.getChildren();

          // Determine if this is a full row merge by checking colspan
          const isFullRowMerge =
            tableCellNode.getColSpan() === tableNode.getColumnCount();

          // For full row merges, apply to first row. For partial merges, apply to last row
          const tableRowIndex = isFullRowMerge
            ? baseRowIndex
            : baseRowIndex + tableCellNode.getRowSpan() - 1;

          if (tableRowIndex >= tableRows.length || tableRowIndex < 0) {
            throw new Error('Expected table cell to be inside of table row.');
          }

          const tableRow = tableRows[tableRowIndex];

          if (!$isTableRowNode(tableRow)) {
            throw new Error('Expected table row');
          }

          const rowCells = tableRow.getChildren<TableCellNode>();
          const measured = Math.max(
            0,
            ...rowCells.map((cell) => getCellNodeHeight(cell, editor) ?? 0),
          );
          let height = tableRow.getHeight();
          // 저장된 height 가 실제 렌더 높이보다 작으면(엑셀 15pt 등) 델타를 더해도
          // 내용 높이에 묻혀 무반응 — 실측 높이를 기준으로 삼을 것
          if (height === undefined || (measured > 0 && height < measured)) {
            height = measured || MIN_ROW_HEIGHT;
          }

          const newHeight = Math.max(height + heightChange, MIN_ROW_HEIGHT);
          tableRow.setHeight(newHeight);
        },
        { tag: SKIP_SCROLL_INTO_VIEW_TAG },
      );
    },
    [activeCell, editor],
  );

  const getCellNodeHeight = (
    cell: TableCellNode,
    activeEditor: LexicalEditor,
  ): number | undefined => {
    const domCellNode = activeEditor.getElementByKey(cell.getKey());
    return domCellNode?.clientHeight;
  };

  const updateColumnWidth = useCallback(
    (widthChange: number) => {
      if (!activeCell) {
        throw new Error('TableCellResizer: Expected active cell.');
      }
      editor.update(
        () => {
          const tableCellNode = $getNearestNodeFromDOMNode(activeCell.elem);
          if (!$isTableCellNode(tableCellNode)) {
            throw new Error('TableCellResizer: Table cell node not found.');
          }

          const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
          const [tableMap] = $computeTableMapSkipCellCheck(
            tableNode,
            null,
            null,
          );
          const columnIndex = getCellColumnIndex(tableCellNode, tableMap);
          if (columnIndex === undefined) {
            throw new Error('TableCellResizer: Table column not found.');
          }

          const colWidths = tableNode.getColWidths();
          if (!colWidths || colWidths.length === 0) {
            return;
          }
          // 병합 셀의 오른쪽 경계는 그 셀이 덮는 마지막 열의 경계
          const boundaryIndex = Math.min(
            columnIndex + Math.max(1, tableCellNode.getColSpan()) - 1,
            colWidths.length - 1,
          );
          const donorIndex = boundaryIndex + 1;
          const target = colWidths[boundaryIndex];
          if (target === undefined) {
            return;
          }
          // 합계가 표 폭과 다르면 fixed 레이아웃이 비례 배분해 1px 드래그가 1px 이 아니게 됨
          const tableElement = getTableElement(
            tableNode,
            editor.getElementByKey(tableNode.getKey()),
          );
          const renderedWidth = tableElement?.clientWidth ?? 0;
          const widthSum = colWidths.reduce((a, w) => a + (w || 0), 0);
          const scale =
            renderedWidth > 0 && widthSum > 0 ? widthSum / renderedWidth : 1;

          const change = Math.round(widthChange * scale);
          const newColWidths = [...colWidths];

          if (donorIndex > colWidths.length - 1) {
            // 표 오른쪽 끝 = 표 크기 조절. 마지막 열만 줄이면 그 열이 최소폭까지 찌그러져
            // 글자가 한 자씩 줄바꿈되므로 전 열을 비례로 조절
            const available = getAvailableWidth(editor);
            const minTotal = colWidths.length * MIN_COLUMN_WIDTH;
            // 이미 본문 폭보다 넓은 표(좁은 창에서 열린 문서)를 드래그 한 번에
            // 본문 폭으로 붕괴시키지 않음 — 넓히는 방향만 막고 좁히는 건 자유.
            const maxTotal =
              available > 0
                ? Math.max(minTotal, available, widthSum)
                : widthSum + change;
            const nextTotal = Math.min(
              maxTotal,
              Math.max(minTotal, widthSum + change),
            );
            if (nextTotal === widthSum) {
              return;
            }
            tableNode.setColWidths(distributeToTotal(colWidths, nextTotal));
            return;
          }

          const donor = colWidths[donorIndex];
          if (donor === undefined) {
            return;
          }
          // 안쪽 경계는 이웃 열과 주고받아 표 전체 폭 유지
          // (TinyMCE table_column_resizing:'preserve' 와 동일)
          const delta = Math.max(
            -(target - MIN_COLUMN_WIDTH),
            Math.min(donor - MIN_COLUMN_WIDTH, change),
          );
          if (delta === 0) {
            return;
          }
          newColWidths[boundaryIndex] = target + delta;
          newColWidths[donorIndex] = donor - delta;
          tableNode.setColWidths(newColWidths);
        },
        { tag: SKIP_SCROLL_INTO_VIEW_TAG },
      );
    },
    [activeCell, editor],
  );

  const pointerUpHandler = useCallback(
    (direction: PointerDraggingDirection) => {
      const handler = (event: PointerEvent) => {
        event.preventDefault();
        event.stopPropagation();

        if (!activeCell) {
          throw new Error('TableCellResizer: Expected active cell.');
        }

        if (pointerStartPosRef.current) {
          const { x, y } = pointerStartPosRef.current;

          if (activeCell === null) {
            return;
          }
          const zoom = calculateZoomLevel(event.target as Element);

          if (isHeightChanging(direction)) {
            const heightChange = (event.clientY - y) / zoom;
            updateRowHeight(heightChange);
          } else {
            const widthChange = (event.clientX - x) / zoom;
            updateColumnWidth(widthChange);
          }

          resetState();
          document.removeEventListener('pointerup', handler);
        }
      };
      return handler;
    },
    [activeCell, resetState, updateColumnWidth, updateRowHeight],
  );

  const toggleResize = useCallback(
    (
        direction: PointerDraggingDirection,
      ): PointerEventHandler<HTMLDivElement> =>
      (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (!activeCell) {
          throw new Error('TableCellResizer: Expected active cell.');
        }

        pointerStartPosRef.current = {
          x: event.clientX,
          y: event.clientY,
        };
        updatePointerCurrentPos(pointerStartPosRef.current);
        updateDraggingDirection(direction);

        document.addEventListener('pointerup', pointerUpHandler(direction));
      },
    [activeCell, pointerUpHandler],
  );

  const getResizers = useCallback(() => {
    if (activeCell) {
      const { height, width, top, left } =
        activeCell.elem.getBoundingClientRect();
      const zoom = calculateZoomLevel(activeCell.elem);
      const zoneWidth = 16; // Pixel width of the zone where you can drag the edge
      const styles: Record<string, CSSProperties> = {
        bottom: {
          backgroundColor: 'none',
          cursor: 'row-resize',
          height: `${zoneWidth}px`,
          left: `${window.scrollX + left}px`,
          top: `${window.scrollY + top + height - zoneWidth / 2}px`,
          width: `${width}px`,
        },
        right: {
          backgroundColor: 'none',
          cursor: 'col-resize',
          height: `${height}px`,
          left: `${window.scrollX + left + width - zoneWidth / 2}px`,
          top: `${window.scrollY + top}px`,
          width: `${zoneWidth}px`,
        },
      };

      const tableRect = tableRectRef.current;

      if (draggingDirection && pointerCurrentPos && tableRect) {
        if (isHeightChanging(draggingDirection)) {
          styles[draggingDirection].left = `${
            window.scrollX + tableRect.left
          }px`;
          styles[draggingDirection].top = `${
            window.scrollY + pointerCurrentPos.y / zoom
          }px`;
          styles[draggingDirection].height = '3px';
          styles[draggingDirection].width = `${tableRect.width}px`;
        } else {
          styles[draggingDirection].top = `${window.scrollY + tableRect.top}px`;
          styles[draggingDirection].left = `${
            window.scrollX + pointerCurrentPos.x / zoom
          }px`;
          styles[draggingDirection].width = '3px';
          styles[draggingDirection].height = `${tableRect.height}px`;
        }

        styles[draggingDirection].backgroundColor = '#adf';
        styles[draggingDirection].mixBlendMode = 'unset';
      }

      return styles;
    }

    return {
      bottom: null,
      left: null,
      right: null,
      top: null,
    };
  }, [activeCell, draggingDirection, pointerCurrentPos]);

  const resizerStyles = getResizers();

  return (
    <div ref={resizerRef}>
      {activeCell != null && (
        <>
          <div
            className="TableCellResizer__resizer TableCellResizer__ui"
            style={resizerStyles.right || undefined}
            onPointerDown={toggleResize('right')}
          />
          <div
            className="TableCellResizer__resizer TableCellResizer__ui"
            style={resizerStyles.bottom || undefined}
            onPointerDown={toggleResize('bottom')}
          />
        </>
      )}
    </div>
  );
}

export default function TableCellResizerPlugin(): null | ReactPortal {
  const [editor] = useLexicalComposerContext();
  const isEditable = useLexicalEditable();

  return useMemo(
    () =>
      isEditable
        ? createPortal(<TableCellResizer editor={editor} />, document.body)
        : null,
    [editor, isEditable],
  );
}
